import React, { useState, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@xinosolutions/xs-auth-react";
import AppHeader from "./AppHeader";
import AppSidebar from "./AppSidebar";
import { styled } from "@mui/material/styles";
import { SIDEBAR_WIDTH } from "../../utils/layoutUtils";
import AppLoader from "../../components/GeneralComponents/AppLoader";
import { useProductMember } from "../../context/ProductContext";

const APP_BAR_MOBILE = 64;
const APP_BAR_DESKTOP = 65;

const RootStyle = styled("div")({
  display: "flex",
  minHeight: "100%",
  overflow: "visible",
});

const MainStyle = styled("div")(({ theme, pathname }) => ({
  flexGrow: 1,
  overflow: pathname.includes("dashboard") ? "visible" : "auto",
  minHeight: "100%",
  paddingTop: APP_BAR_MOBILE,
  paddingBottom: theme.spacing(10),
  [theme.breakpoints.up("lg")]: {
    paddingTop: APP_BAR_DESKTOP,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
}));

/**
 * Dashboard shell — auth gate matches xs-template-web-app (XS Auth only).
 * Product session (member_session → member_init) loads in ProductProvider.
 */
export default function DashboardLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(SIDEBAR_WIDTH);

  const { isAuthenticated, loading: authLoading } = useAuth();
  const { loading: productLoading } = useProductMember();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handlePermanentDrawer = () => {
    if (drawerWidth) {
      setDrawerWidth(0);
    } else {
      setDrawerWidth(SIDEBAR_WIDTH);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  if (authLoading || (isAuthenticated && productLoading)) {
    return <AppLoader wrapperClassName="circular-progress" />;
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <RootStyle>
      <AppHeader
        drawerWidth={drawerWidth}
        handleDrawerToggle={handleDrawerToggle}
        handlePermanentDrawer={handlePermanentDrawer}
      />
      <AppSidebar
        drawerWidth={drawerWidth}
        handleDrawerToggle={handleDrawerToggle}
        mobileOpen={mobileOpen}
      />
      <MainStyle pathname={pathname}>
        <Outlet />
      </MainStyle>
    </RootStyle>
  );
}
